import { Service, TenantBusiness } from "@turizi/shared";
import { ServiceTitle, ServiceTitleStatic } from "./services/service-title";
import { applyDiscount, formatDayShort, formatPrice, todayStart } from "@/lib/format-utils";
import { getServiceSellPrice } from "@/lib/models-utils";
import { TagIcon } from "@/lib/tag-utils";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mantine/core";
import { IconStar, IconWorld, IconInfinity, IconEyeClosed, IconBuildingStore, IconShoppingCartCancel, IconUsers, IconLockAccess, IconLockAccessOff, IconGauge } from "@tabler/icons-react";
import { wrapInLink } from "@/lib/draw-utils";
import { SERVICES_PATH } from "@/lib/navigation";
import { localTenant } from "@/services/session-service";

interface Props {
    service: Service;
    onSelect?: (service: Service) => void;
    start_day?: string;
    end_day?: string;
    className?: string;
    showProviders?: boolean;
    showSoldBy?: boolean;
    iconSize?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    hidePrices?: boolean;
    paxCount?: number;
    client?: TenantBusiness;
    showInfoIcons?: boolean;
}

export default function ServiceCardCatalog({
    service,
    onSelect,
    start_day = todayStart(),
    className,
    showProviders = false,
    showSoldBy = false,
    iconSize = 'xs',
    hidePrices = false,
    paxCount = 0,
    client,
    showInfoIcons = true
}: Props) {
    const { t } = useTranslation();

    const price = getServiceSellPrice(service, start_day)?.[0];

    const getDiscountPercentage = (currency: string): number | null => {
        const clientDiscountCategory = client?.discount_category;
        if (!clientDiscountCategory || !service.discounts) {
            return null;
        }
        const discountCategory = service.discounts.find(
            discount => discount.category === clientDiscountCategory
        );
        if (!discountCategory || !discountCategory.discounts.length) {
            return null;
        }
        return discountCategory.discounts.find(discount => discount.currency === currency)?.percentage || null;
    }

    const discountPercentage = getDiscountPercentage(price?.default_currency || '--');

    const daily_available = service.availability?.daily_available;
    const days = service.availability?.days;
    const hasMultipleDays = days && Object.keys(days).length > 1;

    if (!service) {
        return null;
    }

    const tenant = localTenant();
    const extUrl = `${tenant?.settings?.store?.url}/service/${service?.id}`;
    const hasAnyDayAvailable = days && Object.keys(days).length > 0
        ? Object.values(days).some(limit => limit === null || limit === undefined || limit >= paxCount)
        : (daily_available === null || daily_available === undefined || daily_available >= paxCount);

    const paxsOverflow = paxCount > 0 && !hasAnyDayAvailable;

    return wrapInLink(
        <div
            key={service.id}
            onClick={(e) => {
                if (paxsOverflow) return;
                e.stopPropagation();
                onSelect?.(service);
            }}
            className={`
                ${paxsOverflow ? 'opacity-50 cursor-not-allowed' : ''}
                ${className || 'solid-box-border-hover'}
                flex flex-col gap-3
            `}
        >
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                {/* Title & Details */}
                <div className="flex items-start gap-2 w-full sm:w-auto flex-1 min-w-0">
                    <div className="flex flex-col items-center gap-2 p-2 shrink-0">
                        <TagIcon
                            name={service.category}
                            hideText
                            size={iconSize}
                            noBackground
                        />
                        <div className="text-gray-400 text-xxs text-center">
                            <span className="text-gray-400 text-[0.5rem]">#</span>{service.id}
                        </div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <ServiceTitle
                            service={service}
                            hideProviders={!showProviders}
                            links={false} />
                        {service?.child_services?.map(child_service => (
                            <div
                                key={child_service.id}
                                className="text-gray-400 text-xxs"
                            >
                                #{child_service.id} - {ServiceTitleStatic(child_service)}
                            </div>
                        ))}
                        {hasMultipleDays && days ? (
                            <div className="flex flex-wrap gap-1 mt-3">
                                {Object.entries(days).map(([date, limit]) => {
                                    const isDayAvailable = limit === null || limit === undefined || limit >= paxCount;
                                    return (
                                        <div
                                            key={date}
                                            className={`
                                                text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1 font-sans
                                                ${!isDayAvailable
                                                    ? 'text-red-600 bg-red-50/50'
                                                    : 'text-green-700 bg-green-50/50'}
                                            `}
                                        >
                                            <span className="font-light uppercase tracking-wider text-[9px]">{formatDayShort(date)}</span>
                                            <span className="opacity-40 text-[9px] font-light">|</span>
                                            <span className="font-medium">
                                                {limit !== null && limit !== undefined ? limit : <IconInfinity size={9} className="inline align-middle" />}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <>
                                {daily_available !== undefined && daily_available !== null && (
                                    <div className={`
                                        mt-3 bg-gray-50 rounded-md px-2 py-1 w-fit text-xxs font-medium 
                                        ${daily_available < paxCount ? 'text-red-500' : 'text-green-500'}
                                    `}>
                                        {daily_available} {t('available_places')}
                                    </div>
                                )}
                                {daily_available === null && (
                                    <div className="flex gap-1 items-center mt-3 bg-gray-50 rounded-md px-2 py-1 w-fit text-xxs font-medium text-green-500">
                                        <IconInfinity size={14} stroke={1.5} /> {t('unlimited')}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>

                {/* Price (Under name on mobile, right-aligned on desktop) */}
                {!hidePrices && (
                    <div className="space-y-1 sm:text-right text-left pl-10 sm:pl-0 shrink-0">
                        <div className="text-xxs text-gray-500">{t('price_by_' + service.price_by)}</div>
                        {discountPercentage && price ? (
                            <div className="space-y-0.5">
                                <div className="text-[10px] text-gray-400 italic">
                                    $ {formatPrice(price.default_amount)} {price.default_currency}
                                </div>
                                <div className="flex items-center sm:justify-end justify-start gap-1">
                                    <div className="text-xs text-gray-300 font-semibold">
                                        -{discountPercentage}%
                                    </div>
                                    <div className="flex items-baseline sm:justify-end justify-start gap-1 text-green-800 font-bold">
                                        <span className="text-xs">$</span>{formatPrice(applyDiscount(price.default_amount, discountPercentage))} <span className="text-xs text-gray-500">{price.default_currency}</span>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-baseline sm:justify-end justify-start gap-1 font-semibold">
                                <span className="text-gray-400">$</span>{formatPrice(price?.default_amount)} <span className="text-gray-400">{price?.default_currency}</span>
                            </div>
                        )}
                        <div className="text-[10px] text-gray-400 lowercase mt-0.5">
                            {price?.start_day || price?.end_day
                                ? `${price.start_day ? formatDayShort(price.start_day) : '*'} ${price.end_day ? `- ${formatDayShort(price.end_day)}` : ''}`
                                : t('---')}
                        </div>
                    </div>
                )}

                {/* Additional Info Badges */}
                {showInfoIcons && (
                    <div className="flex flex-col gap-2 sm:items-end items-start pl-10 sm:pl-0 shrink-0">
                        <div className="flex flex-wrap gap-0.5">

                            {/* is Favorite */}
                            {service.is_favorite && (
                                <div className="flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-100">
                                    <IconStar size={14} stroke={1.5} />
                                </div>
                            )}

                            {/* Limit */}
                            <Tooltip label={t('daily_limit')}>
                                <div className="flex gap-1 items-center px-2 py-0.5 text-xs border border-blue-100 bg-blue-50 text-blue-600 rounded-md">
                                    <IconGauge size={14} stroke={1.5} className="text-blue-300" />
                                    {
                                        (service.default_limit === undefined || service.default_limit === null)
                                            ? <IconInfinity size={14} stroke={3} />
                                            : service.default_limit
                                    }
                                </div>
                            </Tooltip>

                            {/* Publish Price Status */}
                            <Tooltip label={service.publish_price ? t('published') : t('internal_use')}>
                                <div className={`
                                flex items-center gap-1 
                                px-2 py-0.5 rounded text-xs 
                                font-medium 
                                ${service.publish_price
                                        ? "bg-green-50 text-green-700 border border-green-100"
                                        : "bg-gray-50 text-gray-700 border border-gray-100"
                                    }
                            `}>
                                    {
                                        service.publish_price
                                            ? (
                                                <IconWorld size={14} stroke={1.5}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        window.open(extUrl, '_blank')
                                                    }}
                                                />
                                            )
                                            : <IconEyeClosed size={14} stroke={1.5} />
                                    }
                                </div>
                            </Tooltip>


                            {/* Publish Price Status */}
                            <Tooltip label={service.available_3rd_party ? t('available_3rd_party') : t('not_available_3rd_party')}>
                                <div className={`
                                flex items-center gap-1 
                                px-2 py-0.5 rounded text-xs 
                                font-medium 
                                ${service.available_3rd_party
                                        ? "bg-orange-50 text-orange-700 border border-orange-100"
                                        : "bg-gray-50 text-gray-700 border border-gray-100"
                                    }
                            `}>
                                    {
                                        service.available_3rd_party
                                            ? (
                                                <IconLockAccess size={14} stroke={1.5}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        window.open(extUrl, '_blank')
                                                    }}
                                                />
                                            )
                                            : <IconLockAccessOff size={14} stroke={1.5} />
                                    }
                                </div>
                            </Tooltip>

                        </div>
                        <div className="flex flex-wrap gap-1">
                            <Tooltip label={t('min_max_pax_per_booking')} >
                                <div className="text-xxs flex gap-1 items-center bg-gray-50 py-1 px-2 rounded border border-gray-100">
                                    <IconUsers size={12} stroke={1} color="gray" />
                                    <span className="text-gray-400">{service.min_pax_per_booking || '-'}</span>
                                    <span className="text-gray-400">/</span>
                                    <span className="text-gray-400">{service.max_pax_per_booking || '-'}</span>
                                </div>
                            </Tooltip>
                            <Tooltip label={
                                service.providers?.map((provider) => provider.name).join(', ') || t('no_providers')
                            }>
                                <div className={`
                                        text-xxs flex gap-1 items-center py-1 px-2 rounded border
                                        ${service.providers?.length > 0 ? 'bg-gray-50 border-gray-100' : 'text-red-500 bg-red-50 border-red-100'}
                                    `}>
                                    <IconBuildingStore stroke={1} size={12} color="gray" />
                                    {service.providers?.length}
                                </div>
                            </Tooltip>
                        </div>
                    </div>
                )}
            </div>

            {showSoldBy && (
                <div className="flex items-center gap-2">
                    <div className="text-gray-400 text-xxs">
                        {t('sold_by')}
                    </div>
                    <div className="text-gray-400 text-xxs">
                        {service.seller?.name}
                    </div>
                </div>
            )}
        </div>,
        onSelect ? undefined : `${SERVICES_PATH}/${service.id}`
    );
}
